/**
 * ═══════════════════════════════════════════════════════════════════════════
 * useAITesting - WASP Frontend v2.2.0
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * @file useAITesting.ts
 * @version 1.0.0
 * @description Hook para testing en vivo de endpoints de IA
 * @author WASP Development Team
 * 
 * ENDPOINTS BACKEND:
 * - POST /api/ai-endpoints/:id/test
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import { useState, useCallback } from 'react';
import axios from 'axios';
import { AIEndpointTestResponse } from '../types/ai.types';

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURACIÓN
// ═══════════════════════════════════════════════════════════════════════════

const API_BASE_URL = 'https://backend.anubisai.net';
const API_KEY = 'anubis-whatsapp-api-key';

// Helper para obtener token
const getHatateToken = () => {
  return localStorage.getItem('hatate_token') || '';
};

// Helper para headers
const getHeaders = () => ({
  'Authorization': getHatateToken(),
  'X-API-Key': API_KEY,
  'Content-Type': 'application/json'
});

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

export interface TestResult {
  success: boolean;
  response?: string;
  tokens_used?: number;
  response_time?: number;
  error?: string;
  note?: string;
  timestamp: string;
}

export interface TestHistory {
  id: string;
  endpoint_id: string;
  endpoint_name: string;
  test_message: string;
  result: TestResult;
  timestamp: string;
}

interface UseAITestingReturn {
  // Estado
  isLoading: boolean;
  error: string | null;
  lastResult: TestResult | null;
  testHistory: TestHistory[];
  
  // Métodos
  testEndpoint: (endpointId: string, testMessage: string) => Promise<TestResult | null>;
  clearHistory: () => void;
  clearLastResult: () => void;
  getHistoryByEndpoint: (endpointId: string) => TestHistory[];
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════════════

export const useAITesting = (): UseAITestingReturn => {
  // ───────────────────────────────────────────────────────────────────────
  // Estado
  // ───────────────────────────────────────────────────────────────────────
  
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [lastResult, setLastResult] = useState<TestResult | null>(null);
  const [testHistory, setTestHistory] = useState<TestHistory[]>([]);

  // ───────────────────────────────────────────────────────────────────────
  // Test: Probar endpoint con mensaje
  // ───────────────────────────────────────────────────────────────────────
  
  const testEndpoint = useCallback(async (
    endpointId: string,
    testMessage: string
  ): Promise<TestResult | null> => {
    setIsLoading(true);
    setError(null);
    
    const startTime = Date.now();
    
    try {
      const response = await axios.post<AIEndpointTestResponse>(
        `${API_BASE_URL}/api/ai-endpoints/${endpointId}/test`,
        { testMessage },
        { headers: getHeaders() }
      );
      
      const endTime = Date.now();
      const responseTime = endTime - startTime;
      
      if (response.data.success) {
        const result: TestResult = {
          success: true,
          response: response.data.result?.response,
          tokens_used: response.data.result?.tokens_used,
          response_time: response.data.result?.response_time || responseTime,
          note: response.data.note,
          timestamp: response.data.timestamp
        };
        
        setLastResult(result);
        
        // Agregar al historial
        const historyEntry: TestHistory = {
          id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          endpoint_id: endpointId,
          endpoint_name: response.data.endpoint.name,
          test_message: testMessage,
          result: result,
          timestamp: response.data.timestamp
        };
        
        setTestHistory(prev => [historyEntry, ...prev].slice(0, 50)); // Máximo 50 en historial
        
        console.log('[useAITesting] Test completed:', endpointId);
        return result;
      } else {
        throw new Error(response.data.message || 'Test failed');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Error testing endpoint';
      setError(errorMsg);
      
      const errorResult: TestResult = {
        success: false,
        error: errorMsg,
        response_time: Date.now() - startTime,
        timestamp: new Date().toISOString()
      };
      
      setLastResult(errorResult);
      
      // Agregar error al historial también
      const historyEntry: TestHistory = {
        id: `test_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        endpoint_id: endpointId,
        endpoint_name: 'Unknown',
        test_message: testMessage,
        result: errorResult,
        timestamp: new Date().toISOString()
      };
      
      setTestHistory(prev => [historyEntry, ...prev].slice(0, 50));
      
      console.error('[useAITesting] Error testing endpoint:', errorMsg);
      return errorResult;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Clear: Limpiar historial completo
  // ───────────────────────────────────────────────────────────────────────
  
  const clearHistory = useCallback(() => {
    setTestHistory([]);
    console.log('[useAITesting] History cleared');
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Clear: Limpiar último resultado
  // ───────────────────────────────────────────────────────────────────────
  
  const clearLastResult = useCallback(() => {
    setLastResult(null);
    setError(null);
    console.log('[useAITesting] Last result cleared');
  }, []);

  // ───────────────────────────────────────────────────────────────────────
  // Get: Obtener historial de un endpoint específico
  // ───────────────────────────────────────────────────────────────────────
  
  const getHistoryByEndpoint = useCallback((endpointId: string): TestHistory[] => {
    return testHistory.filter(entry => entry.endpoint_id === endpointId);
  }, [testHistory]);

  // ───────────────────────────────────────────────────────────────────────
  // Return
  // ───────────────────────────────────────────────────────────────────────
  
  return {
    // Estado
    isLoading,
    error,
    lastResult,
    testHistory,
    
    // Métodos
    testEndpoint,
    clearHistory,
    clearLastResult,
    getHistoryByEndpoint
  };
};

// ═══════════════════════════════════════════════════════════════════════════
// FIN DEL ARCHIVO
// ═══════════════════════════════════════════════════════════════════════════